import axios from 'axios'
import {AuthModel, UserModel} from './_models'
import {getAdAttribution} from '../../../lib/adAttribution'

const API_URL = process.env.REACT_APP_API_URL

export const GET_USER_BY_ACCESSTOKEN_URL = `${API_URL}/verify_token`
export const UPDATE_BOT_URL = `${API_URL}/api/update-bot`
export const GOOGLE_LOGIN_URL = `${API_URL}/api/google-login`
export const GOOGLE_SIGNIN_URL = `${API_URL}/google-signin`
export const BOT_QR_CODE = `${API_URL}/api/bot-qr-code`
export const LOGIN_URL = `${API_URL}/login`
export const REGISTER_URL = `${API_URL}/register`
export const AFFILIATE_REGISTER_URL = `${API_URL}/public/affiliate-register`
export const TRACK_REFERRAL_CLICK_URL = `${API_URL}/public/track-referral-click`
export const YAD2_AGENTS_AUTOCOMPLETE_URL = `${API_URL}/public/yad2-agents/autocomplete`
export const REQUEST_PASSWORD_URL = `${API_URL}/reset-password`
export const RESET_PASSWORD_CHANGE_URL = `${API_URL}/reset-password-change`
export const FORGOT_PASSWORD_URL = `${API_URL}/forgot_password`
export const VERIFY_RESET_CODE_URL = `${API_URL}/verify_reset_code`
export const PHONE_SEND_CODE_URL = `${API_URL}/phone/send-code`
export const PHONE_VERIFY_CODE_URL = `${API_URL}/phone/verify-code`
export const FACEBOOK_SIGNIN_URL = `${API_URL}/facebook-signin`

// Server should return AuthModel
export function login(email: string, password: string) {
  return axios.post<AuthModel>(LOGIN_URL, {
    email,
    password,
  })
}

// Server should return AuthModel
export function register(
  yourname: string,
  phonenumber: string,
  email: string,
  password: string,
  changepassword: string
) {
  return axios.post(REGISTER_URL, {
    yourname,
    phonenumber,
    email,
    password,
    changepassword,
    attribution: getAdAttribution(),
  })
}

export interface Yad2AgentRow {
  broker_id?: number
  name: string
  phone: string
  license_number?: string
  agency_name?: string
  listings_count?: number
  avatar?: string
}

// Public yad2 agent search for the affiliate register page (no auth).
export function searchYad2Agents(q: string) {
  return axios.get<{results: Yad2AgentRow[]}>(YAD2_AGENTS_AUTOCOMPLETE_URL, {params: {q}})
}

// Public affiliate-referred registration: creates the client account, attributes
// it to the affiliate (aff code), and kicks off the yad2 listing import.
export function affiliateRegister(payload: {
  yourname: string
  phone: string
  email: string
  password: string
  aff?: string
  agent?: Yad2AgentRow | null
  turnstileToken?: string
}) {
  return axios.post<{success: boolean; api_token?: string; message?: string}>(AFFILIATE_REGISTER_URL, {
    ...payload,
    attribution: getAdAttribution(),
  })
}

// Record a click on a referral link (best-effort, public, no auth).
export function trackReferralClick(aff: string) {
  return axios.post<{success: boolean}>(TRACK_REFERRAL_CLICK_URL, {aff})
}

// Server should return object => { result: boolean } (Is Email in DB)
export function requestPassword(email: string) {
  return axios.post<{result: boolean}>(REQUEST_PASSWORD_URL, {
    username: email,
  })
}

// Reset password with key from email
export function resetPasswordChange(key: string, password: string) {
  return axios.post<{success: boolean}>(RESET_PASSWORD_CHANGE_URL, {
    key,
    password,
  })
}

// Send reset code to email
export function forgotPassword(email: string, turnstileToken: string) {
  return axios.post<{ok: boolean; email?: string; error?: string; message?: string}>(FORGOT_PASSWORD_URL, {
    email,
    turnstileToken,
  })
}

// Verify reset code and set new password
export function verifyResetCode(email: string, code: string, newPassword: string) {
  return axios.post<{ok: boolean; error?: string}>(VERIFY_RESET_CODE_URL, {
    email,
    code,
    newPassword,
  })
}

export function getUserByToken(token: string) {
  return axios.post<UserModel>(GET_USER_BY_ACCESSTOKEN_URL, {
    api_token: token,
  })
}

export function updateBot(bot:any) {
  return axios.post<UserModel>(UPDATE_BOT_URL, {
    // api_token: token,
    bot: bot,
  })
}

export function googleLogin(bot:any, code:any) {
  return axios.post<UserModel>(GOOGLE_LOGIN_URL, {
    // api_token: token,
    bot: bot,
    code: code,
  })
}

// Google Sign-In / Sign-Up for user authentication. `aff` (referral code) is forwarded
// so a Google sign-up is attributed to the referrer, same as the email register path.
export function googleSignIn(credential: string, aff?: string) {
  return axios.post<{success: boolean; api_token?: string; error?: string}>(GOOGLE_SIGNIN_URL, {
    credential,
    aff: aff || undefined,
    attribution: getAdAttribution(),
  })
}

// Phone (SMS OTP) login. send-code always responds ok:true (unless the number
// is malformed) so it never reveals which numbers are real customers.
export function sendPhoneCode(phone: string) {
  return axios.post<{ok: boolean; error?: string; message?: string}>(PHONE_SEND_CODE_URL, {phone})
}

export function verifyPhoneCode(phone: string, code: string) {
  return axios.post<{ok: boolean; api_token?: string; error?: string; message?: string}>(PHONE_VERIFY_CODE_URL, {
    phone,
    code,
  })
}

// Facebook Sign-In (needs a configured Facebook App on both ends).
// Also signs up: an unknown Facebook user gets a new account (phone collected by the profile gate).
export function facebookSignIn(accessToken: string, aff?: string) {
  return axios.post<{success: boolean; api_token?: string; error?: string}>(FACEBOOK_SIGNIN_URL, {
    accessToken,
    aff: aff || undefined,
    attribution: getAdAttribution(),
  })
}

export function getQRCode(bot:any) {
  return axios.post<UserModel>(BOT_QR_CODE, {
    // api_token: token,
    bot: bot,
  })
}

// Onboarding API functions
export interface OnboardingState {
  current_step: number
  completed: boolean
  campaign_id: string | null
  selected_groups: string[]
  last_updated: string
}

export interface OnboardingResponse {
  success: boolean
  onboarding: OnboardingState
  message?: string
}

export const ONBOARDING_URL = `${API_URL}/user/onboarding`

export function getOnboardingState() {
  return axios.get<OnboardingResponse>(ONBOARDING_URL)
}

export function updateOnboardingState(data: {
  current_step?: number
  completed?: boolean
  campaign_id?: string | null
  selected_groups?: string[]
}) {
  return axios.put<OnboardingResponse>(ONBOARDING_URL, data)
}

export function resetOnboarding() {
  return axios.delete<OnboardingResponse>(ONBOARDING_URL)
}
